import React, { Children } from 'react';
import { css, cx } from '@linaria/atomic';
import { Node, Text as SlateText } from 'slate';
import urlRegex from 'url-regex';
import type { CommonHTMLAttributes } from '@frontend/types';
import { Text } from '../../text';
import { useTemplateEditorValue, useTemplateSettings } from '../message-template.context';
import { isTagNode } from '../message-template.models';
import { MessageTemplateStyles } from '../message-template.styles';
import { TagType } from '../types';

const marginZeroStyle = css`
  margin: 0px;
`;

const renderNode = (node: Node, linkStyle: string, shortenUrlsTo?: string) => {
  if (SlateText.isText(node)) {
    const text = Node.string(node);
    return shortenUrlsTo ? text.replaceAll(urlRegex(), shortenUrlsTo) : text;
  }

  if (isTagNode(node)) {
    if (node.tag.invalid) {
      // don't render invalid tags
      return '';
    }
    const tag = node.tag as TagType;
    const isUrl = urlRegex({ exact: true, strict: false }).test(tag.value);

    return (
      <Text weight='bold' className={cx(isUrl && linkStyle)} as='span'>
        {isUrl && shortenUrlsTo ? shortenUrlsTo : tag.value}
      </Text>
    );
  }

  // do nothing - unexpected node type
  return '';
};

const renderLine = (nodes: Node[], linkStyle: string, shortenUrlsTo?: string) => {
  if (!nodes.map) {
    console.error('Rendering line is not an array');
    return [<></>];
  }
  return nodes.map((node, idx) =>
    node.children ? (
      <React.Fragment key={idx}>
        {renderNode(node, linkStyle, shortenUrlsTo)}
        {renderLine(node.children as Node[], linkStyle, shortenUrlsTo)}
      </React.Fragment>
    ) : (
      <React.Fragment key={idx}>{renderNode(node, linkStyle, shortenUrlsTo)}</React.Fragment>
    )
  );
};

const isBlankRow = (node: Node) => {
  const children = node.children as Node[];

  return children.length === 1 && SlateText.isText(children[0]) && children[0].text.trim() === '';
};

const trimEmptyRowsEnd = (nodes: Node[]): Node[] => {
  // find last non blank node
  for (let i = nodes.length - 1; i > -1; i--) {
    if (!isBlankRow(nodes[i]!)) {
      return nodes.slice(0, i + 1);
    }
  }

  //all blank
  return [];
};

/**
 * Serializes the editor nodes into a plain text template with the tag keys.
 * @param nodes
 */
const renderPreview = (nodes: Node[], linkStyle: string, trim: boolean, shortenUrlsTo?: string) => {
  const trimmedNodes = trim ? trimEmptyRowsEnd(nodes) : nodes;

  // map the block elements to be lines
  return trimmedNodes.map((node, idx) => {
    const line = renderLine(node.children as Node[], linkStyle, shortenUrlsTo);
    const isBlankLine = line.length === 1 && line[0]?.props?.children === '';
    return isBlankLine ? (
      <br key={idx} />
    ) : (
      <Text key={idx} className={marginZeroStyle}>
        {line}
      </Text>
    );
  });
};

type TemplatePreviewProps = CommonHTMLAttributes & {
  noPreviewText?: string;
  appendWith?: string;
  preserveWhitespace?: boolean;
};

export const TemplatePreview = ({
  noPreviewText = '',
  appendWith,
  preserveWhitespace = false,
  ...props
}: TemplatePreviewProps) => {
  const templatePreviewStyles = MessageTemplateStyles.templatePreview;
  const linkStyle = MessageTemplateStyles.linkStyle;
  const nodes = useTemplateEditorValue();
  const { shortenUrlsTo } = useTemplateSettings();

  // @ts-ignore
  const preview = renderPreview(nodes, linkStyle, !preserveWhitespace, shortenUrlsTo);
  const numberOfPreviewComponents = Children.count(preview);

  if (!numberOfPreviewComponents) {
    return null;
  }

  return (
    <div {...props} className={cx(templatePreviewStyles, props.className)}>
      {numberOfPreviewComponents > 0 && preview}
      {numberOfPreviewComponents > 0 && appendWith && <Text className={marginZeroStyle}>{appendWith}</Text>}
      {numberOfPreviewComponents < 1 && !!noPreviewText && (
        <Text color='light' className={marginZeroStyle}>
          {noPreviewText}
        </Text>
      )}
    </div>
  );
};
